import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import type { Questionnaire } from '@/typings/Questionnaire';
import getFieldValue from '@helpers/questionnaires/getFieldValue';
import evaluateExpression from '@helpers/evaluateExpression';
import * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';
import _ from 'lodash';
import checkFieldValidity from '@helpers/questionnaires/checkFieldValidity';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import trackEvents from '@/helpers/trackEvents';
import useLoadAiAgents from './useLoadAiAgents';
import QuestionnaireContext, { QuestionnaireContextType } from './QuestionnaireContext';
import validateCurrency from './validators/validateCurrency';
import validateIntlAddress from './validators/validateIntlAddress';
import validateName from './validators/validateName';
import validateUSAddress from './validators/validateUSAddress';
import QuestionnaireFormProvider from './QuestionnaireFormProvider/QuestionnaireFormProvider';
import QuestionnaireInspector from './QuestionnaireInspector';

type QuestionnaireProviderProps = {
  aiAutofillActions: string[];
  fieldsByKey: Record<string, Questionnaire.Field>;
  formData: Questionnaire.Questionnaire;
  questionnaireResponses: QuestionnaireContextType['questionnaireResponses'];
  onSubmit: (responses: QuestionnaireApiModels.UpdateResponseData, isCompleted?: boolean) => Promise<boolean>;
  userWorkflowId: string | null;
};

const QuestionnaireProvider: React.FC<QuestionnaireProviderProps> = ({
  aiAutofillActions,
  fieldsByKey,
  formData,
  questionnaireResponses,
  children,
  onSubmit,
  userWorkflowId,
}) => {
  trackEvents.track('Render QuestionnaireProvider');

  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const fieldsByKeyRef = useRef(fieldsByKey);
  fieldsByKeyRef.current = fieldsByKey;
  const formMethods = useForm({
    values: formValues,
  });

  const { data: creatorCreditors, getData: getCreatorCreditorsFn } = useNoodleApi(tsClient.creators.getCreatorCreditors);

  const aiAgents = useLoadAiAgents({
    creatorId: formData.questionnaireTemplateCreatorId,
    questionnaireTemplateId: formData.questionnaireTemplateId,
  });
  const [aiAgentsReadyToRun, setAiAgentsReadyToRun] = useState(false);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (aiAgents) {
      timer = setTimeout(() => {
        setAiAgentsReadyToRun(true);
      }, 5000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [aiAgents]);

  useEffect(() => {
    const values: Record<string, unknown> = {};
    (questionnaireResponses ?? []).forEach(response => {
      if (response.fieldTemplate.type !== 'list' && response.fieldTemplate.type !== 'table') {
        values[response.fieldTemplate.key] = response.value;
      } else {
        const listResponse = response.listResponses?.reduce(
          (acc: Record<string, unknown>, item) => ({
            ...acc,
            [item.fieldTemplate.key]: item.value,
          }),
          {
            _associatedWithId: response.associatedWithId,
            _existsInDb: true,
            _id: response.id,
          },
        );
        values[response.fieldTemplate.key] = values[response.fieldTemplate.key]
          ? [...(values[response.fieldTemplate.key] as Array<unknown>), listResponse]
          : [listResponse];
      }
    });
    setFormValues(values);
    formMethods.reset(values);
  }, [formMethods, questionnaireResponses]);

  useEffect(() => {
    if (formData.creatorId) {
      getCreatorCreditorsFn({
        creatorId: formData.creatorId,
        page: 1,
        perPage: 100_000,
      });
    }
  }, [formData.creatorId, getCreatorCreditorsFn]);

  const refetchCreditors = useCallback((): void => {
    if (formData.creatorId) {
      getCreatorCreditorsFn({
        creatorId: formData.creatorId,
        page: 1,
        perPage: 100_000,
      });
    }
  }, [formData.creatorId, getCreatorCreditorsFn]);

  useEffect(() => {
    formData.sections.forEach(section => {
      section.fields.forEach(field => {
        const validationRules: Record<string, unknown> = {};

        if (field.isRequired) {
          validationRules.required = 'This field is required';
        }

        if (field.type === 'name') {
          validationRules.validate = validateName;
        } else if (field.type === 'currency') {
          validationRules.validate = validateCurrency;
        } else if (field.type === 'address-us') {
          validationRules.validate = (value: unknown) => validateUSAddress(value, field.isRequired);
        } else if (field.type === 'address-international') {
          validationRules.validate = validateIntlAddress;
        }

        formMethods.register(field.key, validationRules);
      });
    });
  }, [formMethods, formData.sections]);

  const [currentSection, setCurrentSection] = useState(formData.sections.length > 0 ? formData.sections[0].id : '');
  const [sectionErrors, setSectionErrors] = useState<{ [key: string]: boolean }>({});
  const [referenceData, setReferenceData] = useState<QuestionnaireApiModels.DataReference[]>([]);

  const [sectionVisibility, setSectionVisibilityState] = useState<Record<string, boolean>>({});
  const setSectionVisibility = useCallback((sectionId: string, isVisible: boolean): void => {
    setSectionVisibilityState(prevState => prevState[sectionId] !== isVisible
      ? {
        ...prevState,
        [sectionId]: isVisible,
      }
      : prevState,
    );
  }, []);

  const getSectionProgress = useCallback(
    (sectionId: string, fieldsVisibility: Record<string, boolean>): number => {
      const section = formData.sections.find(_section => _section.id === sectionId);
      if (!section) return 0;

      let totalRequiredFields = section.fields
        .filter(field => !field.listFieldTemplateId && fieldsVisibility[field.key])
        .filter(field => field.isRequired && field.type !== 'explanation').length;

      let completedRequiredFields = 0;

      section.fields
        .filter(field => !field.listFieldTemplateId && fieldsVisibility[field.key])
        .forEach(field => {
          if (field.isRequired) {
            if (field.type === 'list') {
              const fieldValue = (formMethods.getValues()[field.key] || []) as Array<Record<string, unknown>>;
              if (fieldValue.length > 0) {
                completedRequiredFields += 1;
              }
              fieldValue.forEach(listItemValue => {
                if (!listItemValue._associatedWithId) {
                  field.listItemTemplates?.forEach(subField => {
                    let isVisible = true;

                    if (subField.conditionExpression && subField.conditionExpression.expression) {
                      const variablesWithValues = subField.conditionExpression.variables?.map(variable => ({
                        ...variable,
                        value: listItemValue[variable.referenceId],
                      })) || [];

                      const result = evaluateExpression({
                        expression: subField.conditionExpression.expression,
                        id: 'getSectionProgress1',
                        variables: variablesWithValues.reduce(
                          (acc, val) => ({
                            ...acc,
                            [val.variableName]: val.value,
                          }),
                          {},
                        ),
                      });

                      isVisible = Boolean(result);
                    }

                    if (isVisible && subField.isRequired) {
                      totalRequiredFields += 1;
                      const subFieldValue = listItemValue[subField.key];

                      const isFieldValid = checkFieldValidity(subField, subFieldValue);

                      if (isFieldValid) {
                        completedRequiredFields += 1;
                      }
                    }
                  });
                }
              });
            } else if (field.type === 'table') {
              // table is always "complete"
              completedRequiredFields += 1;

              const fieldValue = (formMethods.getValues()[field.key] || []) as Array<Record<string, unknown>>;

              field.tableColumns.forEach((column, idx) => {
                const { conditionExpression } = column;
                let isVisible;
                const conditionVariables = formMethods.watch(
                  _.uniq(
                    field.tableColumns
                      ?.map(c => c.conditionExpression?.variables?.map(v => v.referenceId) || [])
                      .reduce((acc, val) => [...acc, ...val]),
                  ),
                );
                if (conditionExpression?.expression) {
                  const variablesWithValues = conditionExpression.variables?.map((variable, varIdx) => ({
                    ...variable,
                    value: getFieldValue({
                      field: fieldsByKeyRef.current[variable.referenceId],
                      value: conditionVariables[varIdx],
                    }),
                  })) || [];
                  const result = evaluateExpression({
                    expression: conditionExpression.expression,
                    id: 'getSectionProgress2',
                    variables: variablesWithValues.reduce(
                      (acc, val) => ({
                        ...acc,
                        [val.variableName]: val.value,
                      }),
                      {},
                    ),
                  });
                  isVisible = Boolean(result);
                } else {
                  isVisible = true;
                }
                if (isVisible) {
                  field?.listItemTemplates?.forEach(subField => {
                    if (fieldsVisibility[subField.key] && subField.isRequired) {
                      totalRequiredFields += 1;
                      const subFieldValue = fieldValue[idx]?.[subField.key];

                      const isFieldValid = checkFieldValidity(subField, subFieldValue);

                      if (isFieldValid) {
                        completedRequiredFields += 1;
                      }
                    }
                  });
                }
              });
            } else if (field.type === 'median-income' || field.type === 'monthly-income') {
              // median and monthly income are always "complete"
              completedRequiredFields += 1;
            } else {
              const fieldKey = field.key;
              const fieldValue = formMethods.getValues()[fieldKey];

              const isFieldValid = checkFieldValidity(field, fieldValue);

              if (isFieldValid) {
                completedRequiredFields += 1;
              }
            }
          }
        });

      return totalRequiredFields === 0 ? 100 : (completedRequiredFields / totalRequiredFields) * 100;
    },
    [formData, formMethods],
  );

  const getSubsectionProgress = useCallback(
    (
      sectionId: string,
      subsection: string,
      fieldsVisibility: Record<string, boolean>,
    ): {
      completedFields: number;
      subsectionProgressPercentage: number | null;
      totalFields: number;
    } | null => {
      const section = formData.sections.find(_section => _section.id === sectionId);
      if (!section) return null;

      const fieldsInSubsectionKeys = section.fields
        .filter(field => field.subsection === subsection && fieldsVisibility[field.key] && field.type !== 'explanation' && field.isRequired)
        .map(field => field.key);

      const subsectionFieldsVisibility = Object.fromEntries(Object.entries(fieldsVisibility).filter(([key]) => fieldsInSubsectionKeys.includes(key)));

      // reuse getSectionProgress by passing the fields in the subsection
      const subsectionProgressPercentage = fieldsInSubsectionKeys.length ? getSectionProgress(sectionId, subsectionFieldsVisibility) : null;

      // calculate completedFields and totalFields based on the percentage
      const totalFields = fieldsInSubsectionKeys.length;
      const completedFields = subsectionProgressPercentage ? Math.round((subsectionProgressPercentage / 100) * totalFields) : 0;

      return {
        completedFields,
        subsectionProgressPercentage,
        totalFields,
      };
    },
    [formData, getSectionProgress],
  );

  const fieldsById: Record<string, Questionnaire.Field> = useMemo(
    () =>
      Object.values(fieldsByKey).reduce(
        (acc, val) => ({
          ...acc,
          [val.id]: val,
        }),
        {},
      ),
    [fieldsByKey],
  );

  const values = useMemo(() => {
    trackEvents.track('QuestionnaireContext - valuesMemoized');
    const newContextValues: QuestionnaireContextType = {
      aiAgents,
      aiAgentsReadyToRun,
      aiAutofillActions,
      checkFieldValidity,
      creditors: creatorCreditors?.items ?? [],
      currentSection,
      defaultValues: formValues,
      fieldsById,
      fieldsByKey,
      formData,
      getSectionProgress,
      getSubsectionProgress,
      onSubmit,
      questionnaireId: formData.id,
      questionnaireResponses,
      referenceData,
      refetchCreditors,
      sectionErrors,
      sectionVisibility,
      setCurrentSection,
      setReferenceData,
      setSectionErrors,
      setSectionVisibility,
      userWorkflowId,
    };
    return newContextValues;
  }, [
    aiAgents,
    aiAgentsReadyToRun,
    aiAutofillActions,
    creatorCreditors,
    currentSection,
    fieldsById,
    fieldsByKey,
    formData,
    formValues,
    getSectionProgress,
    getSubsectionProgress,
    onSubmit,
    questionnaireResponses,
    referenceData,
    refetchCreditors,
    sectionErrors,
    sectionVisibility,
    setCurrentSection,
    setReferenceData,
    setSectionErrors,
    setSectionVisibility,
    userWorkflowId,
  ]);

  return (
    <QuestionnaireContext.Provider value={values}>
      <QuestionnaireFormProvider
        formMethods={formMethods}
        id="Global"
      >
        <QuestionnaireInspector
          fieldsByKey={fieldsByKey}
          formMethods={formMethods}
          sections={formData.sections}
        />
        {children}
      </QuestionnaireFormProvider>
    </QuestionnaireContext.Provider>
  );
};

export default QuestionnaireProvider;
